/**
 * Widget Editor Page
 * ვიჯეტის რედაქტორი - ვიზუალური კონფიგურაცია
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Input,
  Select,
  Checkbox,
  Spinner,
  useNotification
} from '@aiia/portal-core';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { useApiKeys } from '../hooks/useApiKeys';
import {
  useWidget,
  useCreateWidget,
  useUpdateWidget,
  useAssignWidgetPrompt,
  useUnassignWidgetPrompt,
  useSetWidgetTools,
  useSetWidgetFiles,
} from '../hooks/useWidgets';
import { usePrompts } from '../hooks/usePrompts';
import { useTools } from '../hooks/useTools';
import { useDataFiles } from '../hooks/useDataFiles';
import type { CreateWidgetConfigRequest, QuickAction } from '../features/api/widgets';
import { ChannelManager } from '../components/ChannelManager';
import { getApiErrorMessage } from '../utils/errorMessages';

// Tab types
type TabType = 'basic' | 'theme' | 'header' | 'layout' | 'messages' | 'branding' | 'i18n' | 'ai' | 'channels' | 'preview' | 'embed';

// i18n string keys for translation editor
const i18nStringKeys = [
  { key: 'greeting', label: 'Greeting Message' },
  { key: 'placeholder', label: 'Input Placeholder' },
  { key: 'online', label: 'Online Status' },
  { key: 'offline', label: 'Offline Status' },
  { key: 'typing', label: 'Typing Indicator' },
  { key: 'sendButton', label: 'Send Button' },
  { key: 'errorMessage', label: 'Error Message' },
  { key: 'retryButton', label: 'Retry Button' },
  { key: 'poweredBy', label: 'Powered By Text' },
];

interface WidgetFormData {
  name: string;
  apiKeyId: string;
  assistantName: string;
  logoBase64: string;
  greeting: string;
  placeholder: string;
  position: 'bottom-right' | 'bottom-left' | 'center-bottom' | 'inline';
  // Theme
  primaryColor: string;
  primaryHoverColor: string;
  backgroundColor: string;
  textColor: string;
  userMessageColor: string;
  userMessageTextColor: string;
  botMessageColor: string;
  botMessageTextColor: string;
  borderRadius: string;
  fontFamily: string;
  // Behavior
  soundEnabled: boolean;
  autoOpen: boolean;
  autoOpenDelay: number;
  // Layout
  width: number;
  height: number;
  layoutOffsetX: number;
  layoutOffsetY: number;
  // Header
  headerShow: boolean;
  headerShowCloseButton: boolean;
  headerStatusText: string;
  headerAvatarEmoji: string;
  headerBackgroundColor: string;
  // Toggle Button
  toggleSize: number;
  toggleBackgroundColor: string;
  toggleIcon: string;
  toggleShowBadge: boolean;
  togglePulseAnimation: boolean;
  // Messages
  messagesShowTimestamps: boolean;
  messagesTimestampFormat: 'short' | 'long' | 'relative';
  messagesShowBotAvatar: boolean;
  messagesBotAvatarUrl: string;
  messagesBotAvatarEmoji: string;
  messagesTypingText: string;
  // Branding
  brandingCompanyName: string;
  brandingLogoHeight: string;
  brandingShowPoweredBy: boolean;
  brandingPoweredByText: string;
  // i18n
  i18nLanguage: string;
  i18nDirection: 'ltr' | 'rtl';
  i18nStrings: Record<string, string>;
  // Quick Actions
  quickActions: QuickAction[];
  // AI & RAG
  promptId: string;
  toolIds: string[];
  fileIds: string[];
  // Other
  isDefault: boolean;
}

const initialFormData: WidgetFormData = {
  name: '',
  apiKeyId: '',
  assistantName: 'AI Assistant',
  logoBase64: '',
  greeting: 'Hello! How can I help you today?',
  placeholder: 'Type your message...',
  position: 'bottom-right',
  // Theme
  primaryColor: '#3B82F6',
  primaryHoverColor: '#2563EB',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  userMessageColor: '#3B82F6',
  userMessageTextColor: '#ffffff',
  botMessageColor: '#f3f4f6',
  botMessageTextColor: '#1f2937',
  borderRadius: '16px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  // Behavior
  soundEnabled: true,
  autoOpen: false,
  autoOpenDelay: 3000,
  // Layout
  width: 380,
  height: 550,
  layoutOffsetX: 20,
  layoutOffsetY: 20,
  // Header
  headerShow: true,
  headerShowCloseButton: true,
  headerStatusText: 'Online',
  headerAvatarEmoji: '',
  headerBackgroundColor: '',
  // Toggle Button
  toggleSize: 56,
  toggleBackgroundColor: '',
  toggleIcon: '',
  toggleShowBadge: false,
  togglePulseAnimation: true,
  // Messages
  messagesShowTimestamps: false,
  messagesTimestampFormat: 'short',
  messagesShowBotAvatar: false,
  messagesBotAvatarUrl: '',
  messagesBotAvatarEmoji: '',
  messagesTypingText: 'Typing...',
  // Branding
  brandingCompanyName: '',
  brandingLogoHeight: '24px',
  brandingShowPoweredBy: true,
  brandingPoweredByText: 'Powered by AIIA',
  // i18n
  i18nLanguage: 'en',
  i18nDirection: 'ltr',
  i18nStrings: {},
  // Quick Actions
  quickActions: [],
  // AI & RAG
  promptId: '',
  toolIds: [],
  fileIds: [],
  // Other
  isDefault: false,
};

export default function WidgetEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const notification = useNotification();
  const { apiKeys } = useApiKeys();
  const { widget, isLoading: isWidgetLoading } = useWidget(id || '');
  const createWidget = useCreateWidget();
  const updateWidget = useUpdateWidget();
  const assignWidgetPrompt = useAssignWidgetPrompt();
  const unassignWidgetPrompt = useUnassignWidgetPrompt();
  const setWidgetTools = useSetWidgetTools();
  const setWidgetFiles = useSetWidgetFiles();
  const { prompts } = usePrompts();
  const { tools } = useTools();
  const { dataFiles } = useDataFiles();

  const isEditMode = !!id;
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [formData, setFormData] = useState<WidgetFormData>(initialFormData);
  const [copied, setCopied] = useState(false);
  const [widgetDataLoaded, setWidgetDataLoaded] = useState(false);
  // Track what the user explicitly changed in AI & RAG section
  const [promptChanged, setPromptChanged] = useState(false);
  const [toolsChanged, setToolsChanged] = useState(false);
  const [filesChanged, setFilesChanged] = useState(false);

  // Load widget data when editing
  useEffect(() => {
    if (widget) {
      setWidgetDataLoaded(true);
      setFormData({
        name: widget.name,
        apiKeyId: widget.apiKeyId || '',
        assistantName: widget.assistantName,
        logoBase64: widget.logoUrl || '',
        greeting: widget.greeting,
        placeholder: widget.placeholder,
        position: widget.position,
        // Theme
        primaryColor: widget.theme?.primaryColor || initialFormData.primaryColor,
        primaryHoverColor: widget.theme?.primaryHoverColor || initialFormData.primaryHoverColor,
        backgroundColor: widget.theme?.backgroundColor || initialFormData.backgroundColor,
        textColor: widget.theme?.textColor || initialFormData.textColor,
        userMessageColor: widget.theme?.userMessageColor || initialFormData.userMessageColor,
        userMessageTextColor: widget.theme?.userMessageTextColor || initialFormData.userMessageTextColor,
        botMessageColor: widget.theme?.botMessageColor || initialFormData.botMessageColor,
        botMessageTextColor: widget.theme?.botMessageTextColor || initialFormData.botMessageTextColor,
        borderRadius: widget.theme?.borderRadius || initialFormData.borderRadius,
        fontFamily: widget.theme?.fontFamily || initialFormData.fontFamily,
        // Behavior
        soundEnabled: widget.soundEnabled,
        autoOpen: false,
        autoOpenDelay: 3000,
        // Layout
        width: widget.layout?.width || initialFormData.width,
        height: widget.layout?.height || initialFormData.height,
        layoutOffsetX: widget.layout?.offsetX ?? initialFormData.layoutOffsetX,
        layoutOffsetY: widget.layout?.offsetY ?? initialFormData.layoutOffsetY,
        // Header
        headerShow: widget.header?.show ?? initialFormData.headerShow,
        headerShowCloseButton: widget.header?.showCloseButton ?? initialFormData.headerShowCloseButton,
        headerStatusText: widget.header?.statusText || initialFormData.headerStatusText,
        headerAvatarEmoji: widget.header?.avatarEmoji || initialFormData.headerAvatarEmoji,
        headerBackgroundColor: widget.header?.backgroundColor || initialFormData.headerBackgroundColor,
        // Toggle Button
        toggleSize: widget.toggleButton?.size || initialFormData.toggleSize,
        toggleBackgroundColor: widget.toggleButton?.backgroundColor || initialFormData.toggleBackgroundColor,
        toggleIcon: widget.toggleButton?.icon || initialFormData.toggleIcon,
        toggleShowBadge: widget.toggleButton?.showBadge ?? initialFormData.toggleShowBadge,
        togglePulseAnimation: widget.toggleButton?.pulseAnimation ?? initialFormData.togglePulseAnimation,
        // Messages
        messagesShowTimestamps: widget.messages?.showTimestamps ?? initialFormData.messagesShowTimestamps,
        messagesTimestampFormat: widget.messages?.timestampFormat || initialFormData.messagesTimestampFormat,
        messagesShowBotAvatar: widget.messages?.showBotAvatar ?? initialFormData.messagesShowBotAvatar,
        messagesBotAvatarUrl: widget.avatarUrl || widget.messages?.botAvatarUrl || initialFormData.messagesBotAvatarUrl,
        messagesBotAvatarEmoji: widget.messages?.botAvatarEmoji || initialFormData.messagesBotAvatarEmoji,
        messagesTypingText: widget.messages?.typingText || initialFormData.messagesTypingText,
        // Branding
        brandingCompanyName: widget.branding?.companyName || initialFormData.brandingCompanyName,
        brandingLogoHeight: widget.branding?.logoHeight || initialFormData.brandingLogoHeight,
        brandingShowPoweredBy: widget.branding?.showPoweredBy ?? initialFormData.brandingShowPoweredBy,
        brandingPoweredByText: widget.branding?.poweredByText || initialFormData.brandingPoweredByText,
        // i18n
        i18nLanguage: widget.i18n?.language || initialFormData.i18nLanguage,
        i18nDirection: widget.i18n?.direction || initialFormData.i18nDirection,
        i18nStrings: widget.i18n?.strings || initialFormData.i18nStrings,
        // Quick Actions
        quickActions: widget.quickActions || initialFormData.quickActions,
        // AI & RAG
        promptId: widget.promptId || initialFormData.promptId,
        toolIds: widget.toolIds || initialFormData.toolIds,
        fileIds: widget.fileIds || initialFormData.fileIds,
        // Other
        isDefault: widget.isDefault,
      });
    }
  }, [widget]);

  // Handle logo upload and convert to base64
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      notification.error(t('widgetEditor.uploadImageFile'));
      return;
    }

    // Validate file size (max 500KB for base64)
    if (file.size > 500 * 1024) {
      notification.error(t('widgetEditor.imageTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setFormData({ ...formData, logoBase64: base64 });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setFormData({ ...formData, logoBase64: '' });
  };

  // Handle avatar upload and convert to base64
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notification.error(t('widgetEditor.uploadImageFile'));
      return;
    }

    if (file.size > 500 * 1024) {
      notification.error(t('widgetEditor.imageTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setFormData({ ...formData, messagesBotAvatarUrl: base64 });
    };
    reader.readAsDataURL(file);
  };

  const removeAvatar = () => {
    setFormData({ ...formData, messagesBotAvatarUrl: '' });
  };

  // Auto-select first API key if none selected
  useEffect(() => {
    if (apiKeys && apiKeys.length > 0 && !formData.apiKeyId && !isEditMode) {
      const activeKey = apiKeys.find(k => k.status === 'ACTIVE');
      if (activeKey) {
        setFormData(prev => ({ ...prev, apiKeyId: activeKey.id }));
      }
    }
  }, [apiKeys, formData.apiKeyId, isEditMode]);

  const selectedApiKey = useMemo(() => {
    return apiKeys?.find(k => k.id === formData.apiKeyId);
  }, [apiKeys, formData.apiKeyId]);

  const handleSave = async () => {
    try {
      // Filter out empty i18n strings
      const filteredI18nStrings = Object.fromEntries(
        Object.entries(formData.i18nStrings).filter(([, v]) => v && v.trim())
      );

      const data: CreateWidgetConfigRequest & { apiKeyId?: string } = {
        name: formData.name,
        apiKeyId: formData.apiKeyId || undefined,
        assistantName: formData.assistantName,
        logoUrl: formData.logoBase64 || undefined,
        avatarUrl: formData.messagesBotAvatarUrl || undefined,
        greeting: formData.greeting,
        placeholder: formData.placeholder,
        position: formData.position,
        theme: {
          primaryColor: formData.primaryColor,
          primaryHoverColor: formData.primaryHoverColor,
          backgroundColor: formData.backgroundColor,
          textColor: formData.textColor,
          userMessageColor: formData.userMessageColor,
          userMessageTextColor: formData.userMessageTextColor,
          botMessageColor: formData.botMessageColor,
          botMessageTextColor: formData.botMessageTextColor,
          borderRadius: formData.borderRadius,
          fontFamily: formData.fontFamily,
        },
        soundEnabled: formData.soundEnabled,
        isDefault: formData.isDefault,
        header: {
          show: formData.headerShow,
          showCloseButton: formData.headerShowCloseButton,
          statusText: formData.headerStatusText || undefined,
          avatarEmoji: formData.headerAvatarEmoji || undefined,
          backgroundColor: formData.headerBackgroundColor || undefined,
        },
        layout: {
          width: formData.width,
          height: formData.height,
          offsetX: formData.layoutOffsetX,
          offsetY: formData.layoutOffsetY,
        },
        toggleButton: {
          size: formData.toggleSize,
          backgroundColor: formData.toggleBackgroundColor || undefined,
          icon: formData.toggleIcon || undefined,
          showBadge: formData.toggleShowBadge,
          pulseAnimation: formData.togglePulseAnimation,
        },
        messages: {
          showTimestamps: formData.messagesShowTimestamps,
          timestampFormat: formData.messagesTimestampFormat,
          showBotAvatar: formData.messagesShowBotAvatar,
          botAvatarUrl: formData.messagesBotAvatarUrl || undefined,
          botAvatarEmoji: formData.messagesBotAvatarEmoji || undefined,
          typingText: formData.messagesTypingText || undefined,
        },
        branding: {
          companyName: formData.brandingCompanyName || undefined,
          logoUrl: formData.logoBase64 || undefined,
          logoHeight: formData.brandingLogoHeight || undefined,
          showPoweredBy: formData.brandingShowPoweredBy,
          poweredByText: formData.brandingPoweredByText || undefined,
        },
        i18n: {
          language: formData.i18nLanguage || undefined,
          direction: formData.i18nDirection,
          strings: Object.keys(filteredI18nStrings).length > 0 ? filteredI18nStrings : undefined,
        },
        quickActions: formData.quickActions.length > 0 ? formData.quickActions : undefined,
      };

      if (isEditMode && id) {
        // Don't save if widget data hasn't loaded yet (prevents clearing existing associations)
        if (!widgetDataLoaded) {
          notification.error(t('widgetEditor.dataStillLoading'));
          return;
        }
        await updateWidget.mutateAsync({ id, data });
        // Update prompt only if user explicitly changed it
        if (promptChanged) {
          if (formData.promptId) {
            await assignWidgetPrompt.mutateAsync({ widgetId: id, promptId: formData.promptId });
          } else {
            await unassignWidgetPrompt.mutateAsync(id);
          }
        }
        // Update tools only if user explicitly changed them
        if (toolsChanged) {
          await setWidgetTools.mutateAsync({ widgetId: id, toolIds: formData.toolIds });
        }
        // Update files only if user explicitly changed them
        if (filesChanged) {
          await setWidgetFiles.mutateAsync({ widgetId: id, fileIds: formData.fileIds });
        }
        notification.success(t('widgetEditor.widgetUpdated'));
      } else {
        const newWidget = await createWidget.mutateAsync(data);
        // Set prompt for new widget
        if (formData.promptId) {
          await assignWidgetPrompt.mutateAsync({ widgetId: newWidget.id, promptId: formData.promptId });
        }
        // Set tools and files for new widget
        if (formData.toolIds.length > 0) {
          await setWidgetTools.mutateAsync({ widgetId: newWidget.id, toolIds: formData.toolIds });
        }
        if (formData.fileIds.length > 0) {
          await setWidgetFiles.mutateAsync({ widgetId: newWidget.id, fileIds: formData.fileIds });
        }
        notification.success(t('widgetEditor.widgetCreated'));
        navigate(lp('/widgets'));
      }
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const generateEmbedCode = () => {
    if (!selectedApiKey) return '// Please select an API key first';

    const apiKey = selectedApiKey.keyValue || selectedApiKey.keyPrefix + '...';
    const widgetId = id || '<will be generated after save>';

    const isLocalDev = !import.meta.env.VITE_WIDGET_CDN_URL;

    if (isLocalDev) {
      // Local dev: module script + explicit init
      const agentUrl = import.meta.env.VITE_AGENT_URL || 'http://localhost:8081';
      return `<!-- AIIA Chat Widget (dev) -->
<script src="http://localhost:3001/src/index.ts" type="module"></script>
<script type="module">
  import { init } from 'http://localhost:3001/src/index.ts';
  init({ apiKey: '${apiKey}', widgetId: '${widgetId}', agentUrl: '${agentUrl}' });
</script>`;
    }

    const cdnUrl = import.meta.env.VITE_WIDGET_CDN_URL;

    return `<!-- AIIA Chat Widget -->
<script>(function(w,d,k,i){var s=d.createElement('script');s.src='${cdnUrl}';s.onload=function(){w.AIIAWidget.init({apiKey:k,widgetId:i})};d.head.appendChild(s)})(window,document,'${apiKey}','${widgetId}')</script>`;
  };

  const copyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setCopied(true);
      notification.success(t('widgetEditor.embedCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error: unknown) {
      notification.error(t('widgetEditor.copyFailed'));
    }
  };

  if (isEditMode && isWidgetLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'basic' as const, label: t('widgetEditor.tabs.basic') },
    { id: 'theme' as const, label: t('widgetEditor.tabs.theme') },
    { id: 'header' as const, label: t('widgetEditor.tabs.header') },
    { id: 'layout' as const, label: t('widgetEditor.tabs.layout') },
    { id: 'messages' as const, label: t('widgetEditor.tabs.messages') },
    { id: 'branding' as const, label: t('widgetEditor.tabs.branding') },
    { id: 'i18n' as const, label: t('widgetEditor.tabs.i18n') },
    { id: 'ai' as const, label: t('widgetEditor.tabs.ai') },
    { id: 'channels' as const, label: t('widgetEditor.tabs.channels') },
    { id: 'preview' as const, label: t('widgetEditor.tabs.preview') },
    { id: 'embed' as const, label: t('widgetEditor.tabs.embed') },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? t('widgetEditor.editWidget') : t('widgetEditor.createWidget')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('widgetEditor.subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate(lp('/widgets'))}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!formData.name || !formData.apiKeyId || createWidget.isPending || updateWidget.isPending}
          >
            {createWidget.isPending || updateWidget.isPending ? 'Saving...' : t('widgetEditor.saveWidget')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Panel */}
        <Card className="p-6">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.basicSettings')}</h3>
              <Input
                label={t('widgetEditor.widgetName')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('widgetEditor.widgetNamePlaceholder')}
              />

              {/* API Key Selector */}
              <div>
                <Select
                  label={t('widgetEditor.apiKeyLabel')}
                  value={formData.apiKeyId}
                  onChange={(e) => setFormData({ ...formData, apiKeyId: e.target.value })}
                  options={[
                    { value: '', label: t('widgetEditor.selectApiKey') },
                    ...(apiKeys || [])
                      .filter(k => k.status === 'ACTIVE')
                      .map(k => ({
                        value: k.id,
                        label: `${k.name} (${k.keyPrefix}...)`
                      }))
                  ]}
                />
                {(!apiKeys || apiKeys.filter(k => k.status === 'ACTIVE').length === 0) && (
                  <p className="mt-2 text-sm text-amber-600">
                    {t('widgetEditor.noActiveApiKeys')} <a href={lp('/api-keys')} className="underline font-medium">{t('widgetEditor.createOneFirst')}</a>.
                  </p>
                )}
              </div>

              <Input
                label={t('widgetEditor.assistantNameLabel')}
                value={formData.assistantName}
                onChange={(e) => setFormData({ ...formData, assistantName: e.target.value })}
                placeholder={t('widgetEditor.assistantNamePlaceholder')}
              />

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('widgetEditor.assistantLogo')}
                </label>
                {formData.logoBase64 ? (
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg border overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={formData.logoBase64}
                        alt="Logo preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex gap-2">
                      <label className="cursor-pointer px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                        {t('widgetEditor.changeLogo')}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                      >
                        {t('widgetEditor.removeLogo')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-500">{t('widgetEditor.uploadLogo')}</p>
                      <p className="text-xs text-gray-400 mt-1">{t('widgetEditor.logoFormat')}</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('widgetEditor.greetingMessage')}
                </label>
                <textarea
                  value={formData.greeting}
                  onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                  placeholder={t('widgetEditor.greetingPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <Input
                label={t('widgetEditor.inputPlaceholder')}
                value={formData.placeholder}
                onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                placeholder={t('widgetEditor.inputPlaceholderText')}
              />
              <Select
                label={t('widgetEditor.displayMode')}
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value as 'bottom-right' | 'bottom-left' | 'center-bottom' | 'inline' })}
                options={[
                  { value: 'bottom-right', label: t('widgetEditor.positionBottomRight') },
                  { value: 'bottom-left', label: t('widgetEditor.positionBottomLeft') },
                  { value: 'center-bottom', label: t('widgetEditor.positionCenter') },
                  { value: 'inline', label: t('widgetEditor.positionInline') },
                ]}
              />
              {formData.position === 'inline' && (
                <p className="text-xs text-gray-500 mt-2">
                  ჩატი ჩაშენდება გვერდზე toggle ღილაკის გარეშე. ყოველთვის ხილული იქნება.
                </p>
              )}
              {formData.position === 'center-bottom' && (
                <p className="text-xs text-gray-500 mt-2">
                  Floating ღილაკი + ეკრანის ცენტრში modal ჩატი (როგორც Claude, ChatGPT, Gemini).
                </p>
              )}
              <div className="pt-4 space-y-3">
                <Checkbox
                  label={t('widgetEditor.enableSound')}
                  checked={formData.soundEnabled}
                  onChange={(e) => setFormData({ ...formData, soundEnabled: e.target.checked })}
                />
                <Checkbox
                  label={t('widgetEditor.setAsDefault')}
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              </div>

              {/* Quick Actions Editor */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('widgetEditor.quickActions')}
                  </label>
                  {formData.quickActions.length < 5 && (
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        quickActions: [...formData.quickActions, { label: '', message: '', icon: '' }]
                      })}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {t('widgetEditor.addAction')}
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {t('widgetEditor.quickActionsHint')}
                </p>
                {formData.quickActions.length === 0 ? (
                  <div className="text-sm text-gray-400 italic">{t('widgetEditor.noQuickActions')}</div>
                ) : (
                  <div className="space-y-3">
                    {formData.quickActions.map((action, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500">{t('widgetEditor.action', { num: index + 1 })}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newActions = [...formData.quickActions];
                              newActions.splice(index, 1);
                              setFormData({ ...formData, quickActions: newActions });
                            }}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            placeholder="Label"
                            value={action.label}
                            onChange={(e) => {
                              const newActions = [...formData.quickActions];
                              newActions[index] = { ...action, label: e.target.value };
                              setFormData({ ...formData, quickActions: newActions });
                            }}
                          />
                          <Input
                            placeholder="Message"
                            value={action.message}
                            onChange={(e) => {
                              const newActions = [...formData.quickActions];
                              newActions[index] = { ...action, message: e.target.value };
                              setFormData({ ...formData, quickActions: newActions });
                            }}
                          />
                          <Input
                            placeholder="Icon (emoji)"
                            value={action.icon || ''}
                            onChange={(e) => {
                              const newActions = [...formData.quickActions];
                              newActions[index] = { ...action, icon: e.target.value };
                              setFormData({ ...formData, quickActions: newActions });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'theme' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.tabs.theme')}</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.primaryColor')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.primaryHoverColor')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primaryHoverColor}
                      onChange={(e) => setFormData({ ...formData, primaryHoverColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.primaryHoverColor}
                      onChange={(e) => setFormData({ ...formData, primaryHoverColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.backgroundColor')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.backgroundColor}
                      onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.backgroundColor}
                      onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.textColor')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.textColor}
                      onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.textColor}
                      onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.userMessageBg')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.userMessageColor}
                      onChange={(e) => setFormData({ ...formData, userMessageColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.userMessageColor}
                      onChange={(e) => setFormData({ ...formData, userMessageColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.userMessageText')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.userMessageTextColor}
                      onChange={(e) => setFormData({ ...formData, userMessageTextColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.userMessageTextColor}
                      onChange={(e) => setFormData({ ...formData, userMessageTextColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.botMessageBg')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.botMessageColor}
                      onChange={(e) => setFormData({ ...formData, botMessageColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.botMessageColor}
                      onChange={(e) => setFormData({ ...formData, botMessageColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.botMessageText')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.botMessageTextColor}
                      onChange={(e) => setFormData({ ...formData, botMessageTextColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.botMessageTextColor}
                      onChange={(e) => setFormData({ ...formData, botMessageTextColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <Input
                label={t('widgetEditor.borderRadius')}
                value={formData.borderRadius}
                onChange={(e) => setFormData({ ...formData, borderRadius: e.target.value })}
                placeholder="16px"
              />

              <Input
                label={t('widgetEditor.fontFamily')}
                value={formData.fontFamily}
                onChange={(e) => setFormData({ ...formData, fontFamily: e.target.value })}
                placeholder="system-ui, -apple-system, sans-serif"
              />
            </div>
          )}

          {activeTab === 'header' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.headerSettings')}</h3>

              <div className="space-y-3">
                <Checkbox
                  label={t('widgetEditor.showHeader')}
                  checked={formData.headerShow}
                  onChange={(e) => setFormData({ ...formData, headerShow: e.target.checked })}
                />
                <Checkbox
                  label={t('widgetEditor.showCloseButton')}
                  checked={formData.headerShowCloseButton}
                  onChange={(e) => setFormData({ ...formData, headerShowCloseButton: e.target.checked })}
                />
              </div>

              <Input
                label={t('widgetEditor.headerStatusText')}
                value={formData.headerStatusText}
                onChange={(e) => setFormData({ ...formData, headerStatusText: e.target.value })}
                placeholder="Online"
              />

              <Input
                label={t('widgetEditor.headerAvatarEmoji')}
                value={formData.headerAvatarEmoji}
                onChange={(e) => setFormData({ ...formData, headerAvatarEmoji: e.target.value })}
                placeholder="e.g., 🤖"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.headerBgColor')}</label>
                <p className="text-xs text-gray-500 mb-2">{t('widgetEditor.headerBgColorHint')}</p>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.headerBackgroundColor || formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, headerBackgroundColor: e.target.value })}
                    className="w-10 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={formData.headerBackgroundColor}
                    onChange={(e) => setFormData({ ...formData, headerBackgroundColor: e.target.value })}
                    placeholder="Use primary color"
                    className="flex-1"
                  />
                  {formData.headerBackgroundColor && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, headerBackgroundColor: '' })}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'layout' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.layoutSettings')}</h3>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('widgetEditor.width')}
                  type="number"
                  value={formData.width}
                  onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) || 380 })}
                  placeholder="380"
                />
                <Input
                  label={t('widgetEditor.height')}
                  type="number"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) || 550 })}
                  placeholder="550"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('widgetEditor.offsetX')}
                  type="number"
                  value={formData.layoutOffsetX}
                  onChange={(e) => setFormData({ ...formData, layoutOffsetX: parseInt(e.target.value) || 20 })}
                  placeholder="20"
                />
                <Input
                  label={t('widgetEditor.offsetY')}
                  type="number"
                  value={formData.layoutOffsetY}
                  onChange={(e) => setFormData({ ...formData, layoutOffsetY: parseInt(e.target.value) || 20 })}
                  placeholder="20"
                />
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-md font-medium text-gray-900 mb-4">{t('widgetEditor.toggleButton')}</h4>

                <Input
                  label={t('widgetEditor.buttonSize')}
                  type="number"
                  value={formData.toggleSize}
                  onChange={(e) => setFormData({ ...formData, toggleSize: parseInt(e.target.value) || 56 })}
                  placeholder="56"
                />

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('widgetEditor.buttonBgColor')}</label>
                  <p className="text-xs text-gray-500 mb-2">{t('widgetEditor.buttonBgColorHint')}</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.toggleBackgroundColor || formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, toggleBackgroundColor: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={formData.toggleBackgroundColor}
                      onChange={(e) => setFormData({ ...formData, toggleBackgroundColor: e.target.value })}
                      placeholder="Use primary color"
                      className="flex-1"
                    />
                    {formData.toggleBackgroundColor && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, toggleBackgroundColor: '' })}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                <Input
                  label={t('widgetEditor.buttonIcon')}
                  value={formData.toggleIcon}
                  onChange={(e) => setFormData({ ...formData, toggleIcon: e.target.value })}
                  placeholder="e.g., 💬"
                  className="mt-4"
                />

                <div className="mt-4 space-y-3">
                  <Checkbox
                    label={t('widgetEditor.showBadge')}
                    checked={formData.toggleShowBadge}
                    onChange={(e) => setFormData({ ...formData, toggleShowBadge: e.target.checked })}
                  />
                  <Checkbox
                    label={t('widgetEditor.pulseAnimation')}
                    checked={formData.togglePulseAnimation}
                    onChange={(e) => setFormData({ ...formData, togglePulseAnimation: e.target.checked })}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.messageSettings')}</h3>

              <div className="space-y-3">
                <Checkbox
                  label={t('widgetEditor.showTimestamps')}
                  checked={formData.messagesShowTimestamps}
                  onChange={(e) => setFormData({ ...formData, messagesShowTimestamps: e.target.checked })}
                />
              </div>

              {formData.messagesShowTimestamps && (
                <Select
                  label={t('widgetEditor.timestampFormat')}
                  value={formData.messagesTimestampFormat}
                  onChange={(e) => setFormData({ ...formData, messagesTimestampFormat: e.target.value as 'short' | 'long' | 'relative' })}
                  options={[
                    { value: 'short', label: 'Short (10:30 AM)' },
                    { value: 'long', label: 'Long (Jan 27, 10:30 AM)' },
                    { value: 'relative', label: 'Relative (2 min ago)' },
                  ]}
                />
              )}

              <div className="space-y-3 pt-4 border-t">
                <Checkbox
                  label={t('widgetEditor.showBotAvatar')}
                  checked={formData.messagesShowBotAvatar}
                  onChange={(e) => setFormData({ ...formData, messagesShowBotAvatar: e.target.checked })}
                />
              </div>

              {formData.messagesShowBotAvatar && (
                <div className="space-y-4">
                  {/* Bot Avatar Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('widgetEditor.botAvatarUrl')}
                    </label>
                    {formData.messagesBotAvatarUrl ? (
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full border overflow-hidden bg-gray-50 flex items-center justify-center">
                          <img
                            src={formData.messagesBotAvatarUrl}
                            alt="Avatar preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex gap-2">
                          <label className="cursor-pointer px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                            {t('widgetEditor.changeLogo')}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarUpload}
                              className="hidden"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={removeAvatar}
                            className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                          >
                            {t('widgetEditor.removeLogo')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-3 pb-4">
                          <svg className="w-6 h-6 text-gray-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-xs text-gray-500">{t('widgetEditor.uploadAvatar')}</p>
                          <p className="text-xs text-gray-400">{t('widgetEditor.logoFormat')}</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  {/* Or use emoji */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white text-gray-400">{t('widgetEditor.orUseEmoji')}</span>
                    </div>
                  </div>

                  <Input
                    label={t('widgetEditor.botAvatarEmoji')}
                    value={formData.messagesBotAvatarEmoji}
                    onChange={(e) => setFormData({ ...formData, messagesBotAvatarEmoji: e.target.value })}
                    placeholder="e.g., 🤖"
                  />
                </div>
              )}

              <Input
                label={t('widgetEditor.typingText')}
                value={formData.messagesTypingText}
                onChange={(e) => setFormData({ ...formData, messagesTypingText: e.target.value })}
                placeholder="Typing..."
              />
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.brandingTitle')}</h3>

              <Input
                label={t('widgetEditor.companyName')}
                value={formData.brandingCompanyName}
                onChange={(e) => setFormData({ ...formData, brandingCompanyName: e.target.value })}
                placeholder="Your Company"
              />

              <Input
                label={t('widgetEditor.logoHeight')}
                value={formData.brandingLogoHeight}
                onChange={(e) => setFormData({ ...formData, brandingLogoHeight: e.target.value })}
                placeholder="24px"
              />

              <div className="pt-4 border-t space-y-3">
                <Checkbox
                  label={t('widgetEditor.showPoweredBy')}
                  checked={formData.brandingShowPoweredBy}
                  onChange={(e) => setFormData({ ...formData, brandingShowPoweredBy: e.target.checked })}
                />
              </div>

              {formData.brandingShowPoweredBy && (
                <Input
                  label={t('widgetEditor.poweredByText')}
                  value={formData.brandingPoweredByText}
                  onChange={(e) => setFormData({ ...formData, brandingPoweredByText: e.target.value })}
                  placeholder="Powered by AIIA"
                />
              )}
            </div>
          )}

          {activeTab === 'i18n' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.i18nTitle')}</h3>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('widgetEditor.language')}
                  value={formData.i18nLanguage}
                  onChange={(e) => setFormData({ ...formData, i18nLanguage: e.target.value })}
                  placeholder="en"
                />
                <Select
                  label={t('widgetEditor.direction')}
                  value={formData.i18nDirection}
                  onChange={(e) => setFormData({ ...formData, i18nDirection: e.target.value as 'ltr' | 'rtl' })}
                  options={[
                    { value: 'ltr', label: 'Left to Right (LTR)' },
                    { value: 'rtl', label: 'Right to Left (RTL)' },
                  ]}
                />
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-md font-medium text-gray-900 mb-2">{t('widgetEditor.customStrings')}</h4>
                <p className="text-xs text-gray-500 mb-4">
                  Override default text labels (leave empty to use defaults)
                </p>
                <div className="space-y-3">
                  {i18nStringKeys.map(({ key, label }) => (
                    <Input
                      key={key}
                      label={label}
                      value={formData.i18nStrings[key] || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        i18nStrings: { ...formData.i18nStrings, [key]: e.target.value }
                      })}
                      placeholder={`Default: ${key}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.aiTitle')}</h3>
              <p className="text-gray-600 text-sm mb-4">
                Configure how the AI assistant behaves and what knowledge it has access to.
              </p>

              {/* Prompt Selection */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{t('widgetEditor.promptLabel')}</h4>
                    <p className="text-xs text-gray-500">
                      Define how the AI responds to customers
                    </p>
                  </div>
                  {formData.promptId && (
                    <button
                      type="button"
                      onClick={() => { setFormData({ ...formData, promptId: '' }); setPromptChanged(true); }}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {(!prompts || prompts.length === 0) ? (
                  <div className="text-sm text-gray-400 italic py-4 text-center">
                    {t('widgetEditor.noPrompt')}{' '}
                    <a href={lp('/prompts')} className="text-blue-600 hover:underline">{t('widgetEditor.createPromptLink')}</a>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Select
                      value={formData.promptId}
                      onChange={(e) => { setFormData({ ...formData, promptId: e.target.value }); setPromptChanged(true); }}
                      options={[
                        { value: '', label: t('widgetEditor.selectPrompt') },
                        ...prompts.filter(p => p.isActive).map(prompt => ({
                          value: prompt.id,
                          label: prompt.name
                        }))
                      ]}
                    />
                    {formData.promptId && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        {(() => {
                          const selectedPrompt = prompts.find(p => p.id === formData.promptId);
                          return selectedPrompt ? (
                            <>
                              <div className="text-sm font-medium text-gray-900">{selectedPrompt.name}</div>
                              {selectedPrompt.description && (
                                <div className="text-xs text-gray-500 mt-1">{selectedPrompt.description}</div>
                              )}
                              <div className="text-xs text-gray-400 mt-2">
                                Model: {selectedPrompt.model} | Temperature: {selectedPrompt.temperature}
                              </div>
                              <div className="mt-2 p-2 bg-white rounded border text-xs text-gray-600 max-h-24 overflow-y-auto">
                                {selectedPrompt.systemPrompt.substring(0, 200)}
                                {selectedPrompt.systemPrompt.length > 200 && '...'}
                              </div>
                            </>
                          ) : null;
                        })()}
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      Your custom prompt will be wrapped with AIIA safety guidelines to prevent misuse.
                    </p>
                  </div>
                )}
              </div>

              {/* Tools Section */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{t('widgetEditor.toolsLabel')}</h4>
                    <p className="text-xs text-gray-500">
                      Select tools the AI can use (e.g., webhooks, APIs)
                    </p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {formData.toolIds.length} selected
                  </span>
                </div>

                {(!tools || tools.length === 0) ? (
                  <div className="text-sm text-gray-400 italic py-4 text-center">
                    {t('widgetEditor.noToolsAvailable')}{' '}
                    <a href={lp('/tools')} className="text-blue-600 hover:underline">{t('widgetEditor.createToolLink')}</a>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {tools.filter(t => t.enabled).map(tool => (
                      <label
                        key={tool.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.toolIds.includes(tool.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.toolIds.includes(tool.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, toolIds: [...formData.toolIds, tool.id] }); setToolsChanged(true);
                            } else {
                              setFormData({ ...formData, toolIds: formData.toolIds.filter(id => id !== tool.id) }); setToolsChanged(true);
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{tool.toolName}</div>
                          <div className="text-xs text-gray-500">{tool.toolDescription}</div>
                        </div>
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                          {tool.toolCategory}
                        </span>
                      </label>
                    ))}
                    {tools.filter(t => !t.enabled).length > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        {tools.filter(t => !t.enabled).length} disabled tool(s) not shown
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Files Section (RAG) */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{t('widgetEditor.filesLabel')}</h4>
                    <p className="text-xs text-gray-500">
                      Select files for AI to reference when answering questions
                    </p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {formData.fileIds.length} selected
                  </span>
                </div>

                {(!dataFiles || dataFiles.length === 0) ? (
                  <div className="text-sm text-gray-400 italic py-4 text-center">
                    {t('widgetEditor.noFilesAvailable')}{' '}
                    <a href={lp('/data-files')} className="text-blue-600 hover:underline">{t('widgetEditor.uploadFilesLink')}</a>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {dataFiles.filter(f => f.status === 'READY').map(file => (
                      <label
                        key={file.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.fileIds.includes(file.id)
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.fileIds.includes(file.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, fileIds: [...formData.fileIds, file.id] }); setFilesChanged(true);
                            } else {
                              setFormData({ ...formData, fileIds: formData.fileIds.filter(id => id !== file.id) }); setFilesChanged(true);
                            }
                          }}
                          className="w-4 h-4 text-green-600 rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{file.fileName}</div>
                          <div className="text-xs text-gray-500">
                            {file.fileType} • {(file.fileSize / 1024).toFixed(1)} KB
                            {file.chunkCount && ` • ${file.chunkCount} chunks`}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          file.status === 'READY' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {file.status}
                        </span>
                      </label>
                    ))}
                    {dataFiles.filter(f => f.status !== 'READY').length > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        {dataFiles.filter(f => f.status !== 'READY').length} file(s) still processing
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">{t('widgetEditor.aiHowItWorks')}</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>{t('widgetEditor.aiExplanation1')}</li>
                  <li>{t('widgetEditor.aiExplanation2')}</li>
                  <li>{t('widgetEditor.aiExplanation3')}</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'channels' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.channelsTitle')}</h3>
              {isEditMode && id ? (
                <ChannelManager widgetId={id} />
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    Save the widget first to connect messaging channels (Telegram, WhatsApp, etc.)
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.livePreview')}</h3>
              <p className="text-gray-600 text-sm mb-4">
                This is how your widget will appear on your website.
              </p>

              {/* Inline Widget Preview */}
              <div
                className="relative bg-white rounded-lg shadow-lg overflow-hidden mx-auto"
                style={{
                  width: '100%',
                  maxWidth: '380px',
                  height: '500px',
                  borderRadius: formData.borderRadius
                }}
              >
                {/* Widget Header */}
                <div
                  className="p-4 flex items-center gap-3"
                  style={{ backgroundColor: formData.primaryColor }}
                >
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                    {formData.logoBase64 ? (
                      <img src={formData.logoBase64} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    )}
                  </div>
                  <div className="text-white">
                    <div className="font-semibold">{formData.assistantName || 'AI Assistant'}</div>
                    <div className="text-xs opacity-80">Online</div>
                  </div>
                </div>

                {/* Messages Area */}
                <div
                  className="p-4 space-y-3 h-[340px] overflow-y-auto"
                  style={{ backgroundColor: formData.backgroundColor }}
                >
                  <div className="flex gap-2">
                    <div
                      className="max-w-[80%] p-3 rounded-2xl rounded-tl-sm"
                      style={{ backgroundColor: formData.botMessageColor, color: formData.botMessageTextColor }}
                    >
                      {formData.greeting || 'Hello! How can I help you today?'}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div
                      className="max-w-[80%] p-3 rounded-2xl rounded-tr-sm"
                      style={{ backgroundColor: formData.userMessageColor, color: formData.userMessageTextColor }}
                    >
                      Hi, I have a question
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div
                      className="max-w-[80%] p-3 rounded-2xl rounded-tl-sm"
                      style={{ backgroundColor: formData.botMessageColor, color: formData.botMessageTextColor }}
                    >
                      Of course! I'd be happy to help. What would you like to know?
                    </div>
                  </div>
                </div>

                {/* Input Area */}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-white border-t">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder={formData.placeholder || 'Type your message...'}
                      className="flex-1 px-4 py-2 border rounded-full text-sm"
                      style={{ borderColor: '#e5e7eb', color: formData.textColor }}
                      disabled
                    />
                    <button
                      className="p-2 rounded-full text-white"
                      style={{ backgroundColor: formData.primaryColor }}
                      disabled
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Toggle Button Preview */}
              <div className="flex justify-center mt-4">
                <div
                  className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
                  style={{ backgroundColor: formData.primaryColor }}
                >
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'embed' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.embedCode')}</h3>

              {/* Selected API Key Info */}
              {selectedApiKey ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-green-800">
                    Using API Key: <span className="font-medium">{selectedApiKey.name}</span> ({selectedApiKey.keyPrefix}...)
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm text-amber-800">
                    {t('widgetEditor.noApiKeySelected')} <button onClick={() => setActiveTab('basic')} className="underline font-medium">{t('widgetEditor.selectInBasic')}</button>
                  </span>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('widgetEditor.embedCode')}
                  </label>
                  <Button size="sm" variant="secondary" onClick={copyEmbedCode}>
                    {copied ? 'Copied!' : 'Copy Code'}
                  </Button>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{generateEmbedCode()}</code>
                </pre>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">{t('widgetEditor.integrationTitle')}</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>{t('widgetEditor.integrationStep1')}</li>
                  <li>{t('widgetEditor.integrationStep2')} <code className="bg-blue-100 px-1 rounded">{t('widgetEditor.integrationStep2Tag')}</code></li>
                  <li>{t('widgetEditor.integrationStep3')}</li>
                </ol>
              </div>
            </div>
          )}
        </Card>

        {/* Preview Panel */}
        <Card className="p-6 bg-gray-100">
          <h3 className="text-lg font-semibold mb-4">{t('widgetEditor.widgetPreview')}</h3>

          {/* Display mode indicator */}
          <div className="mb-4 text-sm text-gray-600">
            Mode: <span className="font-medium text-gray-900">
              {formData.position === 'bottom-right' && 'Floating - Bottom Right'}
              {formData.position === 'bottom-left' && 'Floating - Bottom Left'}
              {formData.position === 'center-bottom' && 'Centered Modal'}
              {formData.position === 'inline' && 'Embedded Chat'}
            </span>
          </div>

          {/* Center-bottom mode preview */}
          {formData.position === 'center-bottom' && (
            <div className="relative bg-gray-800/50 rounded-lg p-4 min-h-[400px] flex items-end justify-center">
              {/* Simulated page content */}
              <div className="absolute inset-4 bg-gray-200 rounded opacity-30"></div>

              {/* Wide centered chat window */}
              <div
                className="relative bg-white rounded-2xl shadow-2xl overflow-hidden z-10"
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  height: '380px',
                  fontFamily: formData.fontFamily,
                  direction: formData.i18nDirection
                }}
              >
                {/* Header */}
                {formData.headerShow && (
                  <div
                    className="p-3 flex items-center gap-3"
                    style={{ backgroundColor: formData.headerBackgroundColor || formData.primaryColor }}
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                      {formData.headerAvatarEmoji ? (
                        <span className="text-lg">{formData.headerAvatarEmoji}</span>
                      ) : formData.logoBase64 ? (
                        <img src={formData.logoBase64} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 text-white">
                      <div className="font-semibold text-sm">{formData.assistantName || 'AI Assistant'}</div>
                      <div className="text-xs opacity-80">{formData.headerStatusText || 'Online'}</div>
                    </div>
                    {formData.headerShowCloseButton && (
                      <button className="text-white/80 hover:text-white">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}

                {/* Messages */}
                <div className="p-3 space-y-2 h-[240px] overflow-y-auto" style={{ backgroundColor: formData.backgroundColor }}>
                  <div className="flex gap-2">
                    <div className="max-w-[75%] p-2 rounded-xl rounded-tl-sm text-sm" style={{ backgroundColor: formData.botMessageColor, color: formData.botMessageTextColor }}>
                      {formData.greeting || 'Hello! How can I help you?'}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="max-w-[75%] p-2 rounded-xl rounded-tr-sm text-sm" style={{ backgroundColor: formData.userMessageColor, color: formData.userMessageTextColor }}>
                      Hi there!
                    </div>
                  </div>
                </div>

                {/* Input */}
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-white border-t">
                  <div className="flex items-center gap-2">
                    <input type="text" placeholder={formData.placeholder} className="flex-1 px-3 py-1.5 border rounded-full text-sm" disabled />
                    <button className="p-1.5 rounded-full text-white" style={{ backgroundColor: formData.primaryColor }}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Toggle button in corner */}
              <div
                className="absolute bottom-4 right-4 rounded-full shadow-lg flex items-center justify-center"
                style={{
                  width: `${formData.toggleSize}px`,
                  height: `${formData.toggleSize}px`,
                  backgroundColor: formData.toggleBackgroundColor || formData.primaryColor
                }}
              >
                {formData.toggleIcon ? (
                  <span className="text-xl">{formData.toggleIcon}</span>
                ) : (
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            </div>
          )}

          {/* Inline mode preview */}
          {formData.position === 'inline' && (
            <div className="bg-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 mb-2">Embedded in page content:</div>
              <div
                className="bg-white rounded-lg shadow-lg overflow-hidden"
                style={{
                  width: '100%',
                  height: '400px',
                  borderRadius: formData.borderRadius,
                  fontFamily: formData.fontFamily,
                  direction: formData.i18nDirection
                }}
              >
                {/* Same widget content but full width */}
                {formData.headerShow && (
                  <div className="p-3 flex items-center gap-3" style={{ backgroundColor: formData.headerBackgroundColor || formData.primaryColor }}>
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                      {formData.logoBase64 ? (
                        <img src={formData.logoBase64} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      )}
                    </div>
                    <div className="text-white">
                      <div className="font-semibold text-sm">{formData.assistantName || 'AI Assistant'}</div>
                    </div>
                  </div>
                )}
                <div className="p-3 space-y-2 h-[240px]" style={{ backgroundColor: formData.backgroundColor }}>
                  <div className="flex gap-2">
                    <div className="max-w-[80%] p-2 rounded-xl rounded-tl-sm text-sm" style={{ backgroundColor: formData.botMessageColor, color: formData.botMessageTextColor }}>
                      {formData.greeting || 'Hello!'}
                    </div>
                  </div>
                </div>
                <div className="p-2 bg-white border-t">
                  <div className="flex items-center gap-2">
                    <input type="text" placeholder={formData.placeholder} className="flex-1 px-3 py-1.5 border rounded-full text-sm" disabled />
                    <button className="p-1.5 rounded-full text-white" style={{ backgroundColor: formData.primaryColor }}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-2 text-center">No toggle button - always visible</div>
            </div>
          )}

          {/* Standard floating mode preview (bottom-right / bottom-left) */}
          {(formData.position === 'bottom-right' || formData.position === 'bottom-left') && (
          <div
            className="relative bg-white rounded-lg shadow-lg overflow-hidden mx-auto"
            style={{
              width: `${Math.min(formData.width, 360)}px`,
              height: `${Math.min(formData.height, 520)}px`,
              borderRadius: formData.borderRadius,
              fontFamily: formData.fontFamily,
              direction: formData.i18nDirection
            }}
          >
            {/* Widget Header */}
            {formData.headerShow && (
              <div
                className="p-4 flex items-center gap-3"
                style={{ backgroundColor: formData.headerBackgroundColor || formData.primaryColor }}
              >
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                  {formData.headerAvatarEmoji ? (
                    <span className="text-2xl">{formData.headerAvatarEmoji}</span>
                  ) : formData.logoBase64 ? (
                    <img
                      src={formData.logoBase64}
                      alt="Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 text-white">
                  <div className="font-semibold">{formData.assistantName || 'AI Assistant'}</div>
                  <div className="text-xs opacity-80">{formData.headerStatusText || 'Online'}</div>
                </div>
                {formData.headerShowCloseButton && (
                  <button className="text-white/80 hover:text-white">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Messages Area */}
            <div
              className={`p-4 space-y-3 overflow-y-auto ${formData.headerShow ? 'h-[300px]' : 'h-[360px]'}`}
              style={{ backgroundColor: formData.backgroundColor }}
            >
              {/* Bot message */}
              <div className="flex gap-2">
                {formData.messagesShowBotAvatar && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {formData.messagesBotAvatarUrl ? (
                      <img src={formData.messagesBotAvatarUrl} alt="Bot" className="w-full h-full object-cover" />
                    ) : formData.messagesBotAvatarEmoji ? (
                      <span className="text-lg">{formData.messagesBotAvatarEmoji}</span>
                    ) : (
                      <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                )}
                <div>
                  <div
                    className="max-w-[80%] p-3 rounded-2xl rounded-tl-sm"
                    style={{
                      backgroundColor: formData.botMessageColor,
                      color: formData.botMessageTextColor
                    }}
                  >
                    {formData.greeting || 'Hello! How can I help you today?'}
                  </div>
                  {formData.messagesShowTimestamps && (
                    <div className="text-xs text-gray-400 mt-1 ml-1">
                      {formData.messagesTimestampFormat === 'relative' ? '2 min ago' : formData.messagesTimestampFormat === 'long' ? 'Jan 27, 10:30 AM' : '10:30 AM'}
                    </div>
                  )}
                </div>
              </div>

              {/* User message */}
              <div className="flex justify-end">
                <div>
                  <div
                    className="max-w-[80%] p-3 rounded-2xl rounded-tr-sm"
                    style={{
                      backgroundColor: formData.userMessageColor,
                      color: formData.userMessageTextColor
                    }}
                  >
                    Hi, I have a question
                  </div>
                  {formData.messagesShowTimestamps && (
                    <div className="text-xs text-gray-400 mt-1 text-right mr-1">
                      {formData.messagesTimestampFormat === 'relative' ? '1 min ago' : formData.messagesTimestampFormat === 'long' ? 'Jan 27, 10:31 AM' : '10:31 AM'}
                    </div>
                  )}
                </div>
              </div>

              {/* Bot response with typing indicator simulation */}
              <div className="flex gap-2">
                {formData.messagesShowBotAvatar && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {formData.messagesBotAvatarUrl ? (
                      <img src={formData.messagesBotAvatarUrl} alt="Bot" className="w-full h-full object-cover" />
                    ) : formData.messagesBotAvatarEmoji ? (
                      <span className="text-lg">{formData.messagesBotAvatarEmoji}</span>
                    ) : (
                      <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                )}
                <div>
                  <div
                    className="max-w-[80%] p-3 rounded-2xl rounded-tl-sm"
                    style={{
                      backgroundColor: formData.botMessageColor,
                      color: formData.botMessageTextColor
                    }}
                  >
                    Of course! I'd be happy to help.
                  </div>
                  {formData.messagesShowTimestamps && (
                    <div className="text-xs text-gray-400 mt-1 ml-1">
                      {formData.messagesTimestampFormat === 'relative' ? 'just now' : formData.messagesTimestampFormat === 'long' ? 'Jan 27, 10:31 AM' : '10:31 AM'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            {formData.quickActions.length > 0 && (
              <div className="px-3 pb-2 flex flex-wrap gap-2" style={{ backgroundColor: formData.backgroundColor }}>
                {formData.quickActions.slice(0, 3).map((action, idx) => (
                  <button
                    key={idx}
                    className="px-3 py-1.5 text-xs rounded-full border"
                    style={{ borderColor: formData.primaryColor, color: formData.primaryColor }}
                  >
                    {action.icon && <span className="mr-1">{action.icon}</span>}
                    {action.label || `Action ${idx + 1}`}
                  </button>
                ))}
              </div>
            )}

            {/* Powered By Footer */}
            {formData.brandingShowPoweredBy && (
              <div className="text-center py-1 text-xs text-gray-400" style={{ backgroundColor: formData.backgroundColor }}>
                {formData.brandingPoweredByText || 'Powered by AIIA'}
              </div>
            )}

            {/* Input Area */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-white border-t">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder={formData.placeholder || 'Type your message...'}
                  className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: '#e5e7eb',
                    color: formData.textColor
                  }}
                  disabled
                />
                <button
                  className="p-2 rounded-full text-white"
                  style={{ backgroundColor: formData.primaryColor }}
                  disabled
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          )}

          {/* Toggle Button Preview - only for floating modes */}
          {(formData.position === 'bottom-right' || formData.position === 'bottom-left' || formData.position === 'center-bottom') && (
          <div className="mt-6 flex justify-end relative">
            <div
              className={`rounded-full shadow-lg flex items-center justify-center cursor-pointer relative ${formData.togglePulseAnimation ? 'animate-pulse' : ''}`}
              style={{
                width: `${formData.toggleSize}px`,
                height: `${formData.toggleSize}px`,
                backgroundColor: formData.toggleBackgroundColor || formData.primaryColor
              }}
            >
              {formData.toggleIcon ? (
                <span className="text-2xl">{formData.toggleIcon}</span>
              ) : (
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              )}
              {formData.toggleShowBadge && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white" />
              )}
            </div>
          </div>
          )}
        </Card>
      </div>
    </div>
  );
}
